import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { withStyles } from 'tss-react/mui';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Map } from 'immutable';
import { getComboTva } from '@lba-dev/package.local-globals';

import { PAC_AIR_EAU } from '../../../constants/categorie';
import LinearProgressWithLabel from '../../LinearProgressWithLabel';
import Money from '../../../utils/Money';

const styles = ({ palette }) => ({
  gridContainer: {
    padding: 0
  },
  mainBox: {
    padding: 12,
    borderRadius: 5,
    backgroundColor: palette.primary.main,
  },
  textColor: {
    color: palette.common.white,
  },
  childBackground: {
    backgroundColor: palette.common.white,
    borderRadius: 5,
  },
  subChildBackground: {
    backgroundColor: palette.common.white,
    margin: 4
  },
  listIcons: {
    color: palette.grey[500],
    verticalAlign: 'middle',
    fontSize: 18,
    margin: '1%',
  },
  listIconsCheck: {
    color: palette.success.main,
    verticalAlign: 'middle',
    fontSize: 18,
    margin: '1%',
  },
  listIconsClose: {
    color: palette.error.main,
    verticalAlign: 'middle',
    fontSize: 18,
    margin: '1%',
  },
  normalText: {
    fontWeight: 400,
  },
  priceTitle: {
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  priceText: {
    textAlign: 'right',
    paddingRight: 18,
    whiteSpace: 'nowrap'
  },
  esmimateContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
  estimateTitle: {
    textAlign: 'center',
    fontWeight: 300
  },
  estimatedBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  estimatedValue: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: palette.success.main
  }
});

function formatNum(num) {
  if (!num) {
    return 'N/A';
  }
  return new Intl.NumberFormat('fr-FR').format(num);
}

export default withStyles(({
  classes,
  element,
  dp = new Map({}),
  loadingDp,
  statsEnergy
}) => {
  const tva = getComboTva(element, null);
  const optionstatsEnergy = useMemo(() => {
    if (
      !statsEnergy ||
      statsEnergy.size < 4 ||
      !statsEnergy.get('PAC', new Map()).size
    ) {
      return null;
    }
    const axis = Object.keys(statsEnergy.get('PAC', new Map()).toJS());

    const rawData = axis.map(item => [
      statsEnergy.getIn(['PAC', item]),
      statsEnergy.getIn(['Gas', item]),
      statsEnergy.getIn(['Fioul', item]),
      statsEnergy.getIn(['Propane', item])
    ])
    const hashColor = {
      [axis[0]]: '#3b9ca3',
      [axis[1]]: '#f4a582',
    }
    const series = axis.map((name, sid) => ({
      name,
      type: 'bar',
      stack: 'total',
      label: {
        show: true,
        formatter: (params) => formatNum(params.value)
      },
      data: rawData[sid],
      itemStyle: { color: hashColor[name] }
    }))

    const totalValues = rawData[0]
      .map((values, index) => values + rawData[1][index])

    return {
      title: {
        text: 'Comparaison des énergies (€)',
        left: 'center',
        textStyle: { fontSize: 20, fontWeight: 'bold' }
      },
      legend: {
        selectedMode: true,
        top: 30,
        left: 'center'
      },
      yAxis: {
        type: 'value',
        name: 'Coût annuel (€)',
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: { fontSize: 14, fontWeight: 'bold', color: '#555' }
      },
      xAxis: {
        type: 'category',
        data: ['PAC', 'Gaz Naturel', 'Fioul', 'Propane']
      },
      series: [
        ...series,
        {
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            position: 'insideTop',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#000',
            formatter: (params) => formatNum(params.value)
          },
          data: totalValues,
          itemStyle: { color: 'transparent' }
        }
      ]
    };
  }, [statsEnergy])
  return (
    <Grid item xs={4} className={classes.gridContainer}>
      {
        !dp.size && !loadingDp &&
        (
          !element.path?.toLowerCase()
            .includes(PAC_AIR_EAU)
        ) &&
        <Box className={classes.mainBox}>
          <Box p={1}>
            <Typography
              variant="h4"
              className={classes.textColor}
              children={element.description}
            />
          </Box>
          <Divider className={classes.textColor} />
          <Box p={1} mb={3}>
            <Grid container spacing={2} className={classes.childBackground}>
              <Grid item xs={6} className={classes.priceTitle}>
                <Typography
                  color="primary"
                  variant="subtitle2"
                  children={'Le tarif estimatif'}
                  className={classes.normalText}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography
                  color="primary"
                  style={{ fontWeight: 'bold' }}
                  className={classes.priceText}
                  children={`${element.minPrice && element.maxPrice ?
                    `entre ${Money.toString(
                      Math.round(
                        (element.minPrice * (1 + (tva / 100)))
                      )
                    ).slice(0, -3)
                    } € et ${Money.toString(
                      Math.round(
                        element.maxPrice * (1 + (tva / 100)))
                    ).slice(0, -3)} €`
                    : `${element.obligDevis ? 'à partir de' : 'Prix fixe'
                    } ${Money.toString(
                      Math.round(
                        (element.maxPrice || element.minPrice) *
                        (1 + (tva / 100))
                      ),
                      true
                    )}`} (TTC)`
                  }
                  align="center"
                />
              </Grid>
            </Grid>
          </Box>
          <Box p={1}>
            <Grid container spacing={1} className={classes.childBackground}>
              <Grid item xs={12} className={classes.subChildBackground}>
                <Typography
                  variant={'h6'}
                  className={classes.normalText}
                  children="Inclus dans la prestation"
                />
              </Grid>
              <Grid item xs={12} className={classes.subChildBackground}>
                {element.prestations && element.prestations.length ? (
                  element.prestations.map((e) => (
                    <Typography key={`${e.name}-${e._id}`}>
                      <CheckIcon className={classes.listIconsCheck} /> {e.name}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    component="p"
                    variant="caption"
                    children="Aucune prestation"
                  />
                )}
              </Grid>
              {!!(element.notPrestations || []).length && <>
                <Grid item xs={12} className={classes.subChildBackground}>
                  <Typography
                    variant={'h6'}
                    className={classes.normalText}
                    children="Ce qui n'est pas inclus"
                  />
                </Grid>
                <Grid item xs={12} className={classes.subChildBackground}>
                  {element.notPrestations.map((e) => (
                    <Typography key={`notPrestation-${e.name}`}>
                      <CloseIcon className={classes.listIconsClose} />
                      {e.name || e}
                    </Typography>
                  ))}
                </Grid>
              </>}

            </Grid>
          </Box>
        </Box>
      }
      {
        (
          element.path?.toLowerCase()
            .includes(PAC_AIR_EAU)
        ) &&
        <Grid
          container
          rowSpacing={5}
          className={classes.esmimateContainer}
        >
          <Grid item xs={12}>
            <Typography
              className={classes.estimateTitle}
              variant='h4'
            >
              Votre besoin estimé
            </Typography>
          </Grid>
          <Grid item xs={5} className={classes.estimatedBox}>
            <Typography variant='caption'>
              Coefficient de déperditions G
            </Typography>
            <Typography
              variant='h5'
              className={classes.estimatedValue}
            >
              {
                dp.size > 0 && dp.has('G') ?
                  formatNum(dp.get('G', 0))
                  : 'N/A'
              } W/m3/K
            </Typography>
          </Grid>
          <Divider orientation='vertical' flexItem />
          <Grid item xs={5} className={classes.estimatedBox}>
            <Typography variant='caption'>
              Déperditions
            </Typography>
            <Typography
              variant='h5'
              className={classes.estimatedValue}
            >
              {
                dp.size > 0 && dp.has('Deperd') ?
                  formatNum(dp.get('Deperd', 0))
                  : 'N/A'
              } W
            </Typography>
          </Grid>
          {
            loadingDp &&
            <Grid item xs={12}>
              <LinearProgressWithLabel waiting={loadingDp} />
            </Grid>
          }
          {
            statsEnergy.size > 0 && optionstatsEnergy &&
            <Grid item xs={12}>
              <ReactEcharts
                option={optionstatsEnergy}
                lazyUpdate
              />
            </Grid>
          }
        </Grid>
      }
    </Grid>
  )
}, styles);
